import {
  IntegrationReferenceDirection,
  TableMappingType,
  type IntegrationWorkspace,
} from '@ardoq/api-types/integrations';
import type { ColumnMapping } from 'integrations/common/streams/transferConfigs/types';
import {
  COMPONENTS_COLUMN_TYPES,
  COMPONENTS_SINGLE_USE_COLUMN_TYPES,
  REFERENCES_COLUMN_TYPES,
  REFERENCES_SINGLE_USE_COLUMN_TYPES,
  isOneOfColumnTypes,
} from 'integrations/common/components/columnMappingSidebar/columnTypeRules';
import type {
  AutomapColumnMapping,
  AutomapColumnsContext,
  ContextField,
  ContextTable,
  LockedColumn,
} from './types';

const IDENTIFYING_COLUMN_TYPES: ReadonlyArray<ColumnMapping['columnType']> = [
  'component',
  'custom-id',
  'component-path',
  'ardoq-oid',
  'component-key',
];

type ComponentTypeRules = {
  componentTypes: string[];
  canCreateTypes: boolean;
};

type ReferenceSides = {
  rootWorkspace: IntegrationWorkspace;
  targetWorkspace: IntegrationWorkspace | null;
};

export const toColumnNames = (mappings: AutomapColumnMapping[]): string =>
  mappings.map(({ columnName }) => columnName).join(', ');

export const toWorkspaceComponentTypes = (
  { id }: IntegrationWorkspace,
  { workspaces }: AutomapColumnsContext
): string[] =>
  workspaces.find(workspace => workspace.id === id)?.componentTypes ?? [];

const isColumnInRange = (
  columns: string[],
  { columnIndex }: AutomapColumnMapping
): boolean =>
  columnIndex >= 0 &&
  columnIndex < columns.length &&
  Boolean(columns[columnIndex]);

const isDuplicateIndex = (
  { columnIndex }: AutomapColumnMapping,
  position: number,
  mappings: AutomapColumnMapping[]
): boolean =>
  mappings.findIndex(mapping => mapping.columnIndex === columnIndex) !==
  position;

const isAllowedComponentType = (
  { componentTypes, canCreateTypes }: ComponentTypeRules,
  name: string
): boolean =>
  canCreateTypes ? Boolean(name.trim()) : componentTypes.includes(name);

const isKnownComponentType = (
  rules: ComponentTypeRules,
  hasComponentTypeColumn: boolean,
  { mappedTo }: AutomapColumnMapping
): boolean => {
  switch (mappedTo.columnType) {
    case 'component':
      return mappedTo.componentType === null
        ? hasComponentTypeColumn
        : isAllowedComponentType(rules, mappedTo.componentType);
    case 'component-path':
      return mappedTo.componentTypes.every(name =>
        isAllowedComponentType(rules, name)
      );
    default:
      return true;
  }
};

const isKnownField = (
  fields: ContextField[],
  { mappedTo }: AutomapColumnMapping
): boolean => {
  if (
    (mappedTo.columnType !== 'field' && mappedTo.columnType !== 'custom-id') ||
    !mappedTo.existingField
  ) {
    return true;
  }
  const { name, label, type } = mappedTo.existingField;
  return fields.some(
    field => field.name === name && field.label === label && field.type === type
  );
};

const isNewCustomIdField = ({ mappedTo }: AutomapColumnMapping): boolean =>
  mappedTo.columnType === 'custom-id' && !mappedTo.existingField;

const hasReferenceType = (
  { mappedTo }: AutomapColumnMapping,
  hasReferenceTypeColumn: boolean
): boolean => {
  switch (mappedTo.columnType) {
    case 'reference':
      return Boolean(mappedTo.referenceType.trim());
    case 'root-reference':
      return hasReferenceTypeColumn || Boolean(mappedTo.referenceType?.trim());
    default:
      return true;
  }
};

const hasValidHierarchyLevel = ({ mappedTo }: AutomapColumnMapping): boolean =>
  mappedTo.columnType !== 'component' || mappedTo.hierarchyLevel >= 1;

const hasColumnType = (
  mappings: AutomapColumnMapping[],
  columnType: ColumnMapping['columnType']
): boolean =>
  mappings.some(({ mappedTo }) => mappedTo.columnType === columnType);

const toAllowedColumnTypes = (
  rowRepresentation: TableMappingType
): readonly ColumnMapping['columnType'][] =>
  rowRepresentation === TableMappingType.COMPONENTS
    ? COMPONENTS_COLUMN_TYPES
    : REFERENCES_COLUMN_TYPES;

const toSingleUseColumnTypes = (
  rowRepresentation: TableMappingType
): readonly ColumnMapping['columnType'][] =>
  rowRepresentation === TableMappingType.COMPONENTS
    ? COMPONENTS_SINGLE_USE_COLUMN_TYPES
    : REFERENCES_SINGLE_USE_COLUMN_TYPES;

const findRepeatedSingleUseTypes = (
  mappings: AutomapColumnMapping[],
  rowRepresentation: TableMappingType
): string[] =>
  toSingleUseColumnTypes(rowRepresentation).filter(
    columnType =>
      mappings.filter(({ mappedTo }) => mappedTo.columnType === columnType)
        .length > 1
  );

const hasContiguousHierarchy = (mappings: AutomapColumnMapping[]): boolean =>
  mappings
    .flatMap(({ mappedTo }) =>
      mappedTo.columnType === 'component' ? [mappedTo.hierarchyLevel] : []
    )
    .sort((a, b) => a - b)
    .every((level, position) => level === position + 1);

const toReferencedWorkspace = (
  { mappedTo }: AutomapColumnMapping,
  { rootWorkspace, targetWorkspace }: ReferenceSides
): IntegrationWorkspace | null => {
  switch (mappedTo.columnType) {
    case 'reference':
      return mappedTo.targetWorkspace;
    case 'root-reference':
      return rootWorkspace;
    case 'target-reference':
      return targetWorkspace ?? rootWorkspace;
    default:
      return null;
  }
};

const toReferencedComponentType = ({
  mappedTo,
}: AutomapColumnMapping): string | null => {
  switch (mappedTo.columnType) {
    case 'reference':
    case 'root-reference':
    case 'target-reference':
      return mappedTo.componentType;
    default:
      return null;
  }
};

const isLockedColumnMapped = (
  mappings: AutomapColumnMapping[],
  { columnIndex, columnType }: LockedColumn
): boolean =>
  mappings.some(
    mapping =>
      mapping.columnIndex === columnIndex &&
      mapping.mappedTo.columnType === columnType
  );

type LockCheck = Pick<
  ContextTable,
  'columns' | 'lockedRowRepresentation' | 'lockedColumns'
> & {
  mappings: AutomapColumnMapping[];
  rowRepresentation: TableMappingType;
};

export const findLockProblem = ({
  mappings,
  rowRepresentation,
  columns,
  lockedRowRepresentation,
  lockedColumns,
}: LockCheck): string | null => {
  if (
    lockedRowRepresentation &&
    rowRepresentation !== lockedRowRepresentation
  ) {
    return `Rows must be ${lockedRowRepresentation} for this table.`;
  }

  const unmappedLockedColumns = lockedColumns.filter(
    lockedColumn => !isLockedColumnMapped(mappings, lockedColumn)
  );
  if (unmappedLockedColumns.length > 0) {
    return `These columns must be mapped as given: ${unmappedLockedColumns
      .map(
        ({ columnIndex, columnType }) =>
          `${columns[columnIndex]} as '${columnType}'`
      )
      .join(', ')}.`;
  }

  return null;
};

type ColumnCheck = {
  mappings: AutomapColumnMapping[];
  columns: string[];
  rowRepresentation: TableMappingType;
};

export const findColumnProblem = ({
  mappings,
  columns,
  rowRepresentation,
}: ColumnCheck): string | null => {
  const outOfRange = mappings.filter(
    mapping => !isColumnInRange(columns, mapping)
  );
  if (outOfRange.length > 0) {
    return `Unknown column index for columns: ${toColumnNames(outOfRange)}.`;
  }

  const duplicates = mappings.filter(isDuplicateIndex);
  if (duplicates.length > 0) {
    return `Columns mapped more than once: ${toColumnNames(duplicates)}. Map each column once.`;
  }

  const allowed = toAllowedColumnTypes(rowRepresentation);
  const notAllowed = mappings.filter(
    ({ mappedTo }) => !isOneOfColumnTypes(allowed, mappedTo.columnType)
  );
  if (notAllowed.length > 0) {
    return `Column types not allowed when rows are ${rowRepresentation}: ${toColumnNames(notAllowed)}.`;
  }

  return null;
};

export const findSheetStructureProblem = (
  mappings: AutomapColumnMapping[],
  rowRepresentation: TableMappingType
): string | null => {
  const repeated = findRepeatedSingleUseTypes(mappings, rowRepresentation);
  if (repeated.length > 0) {
    return `Map at most one column as: ${repeated.join(', ')}.`;
  }

  if (rowRepresentation === TableMappingType.REFERENCES) {
    return hasColumnType(mappings, 'root-reference') &&
      hasColumnType(mappings, 'target-reference')
      ? null
      : "A references sheet needs one 'root-reference' and one 'target-reference' column.";
  }

  if (
    !IDENTIFYING_COLUMN_TYPES.some(columnType =>
      hasColumnType(mappings, columnType)
    )
  ) {
    return "A components sheet needs a column that identifies its components. Prefer component names as 'component' (hierarchyLevel 1); otherwise use 'custom-id', then 'component-path', 'ardoq-oid' or 'component-key'.";
  }

  if (!hasContiguousHierarchy(mappings)) {
    return "The 'component' columns' hierarchy levels must start at 1 with no gaps or repeats.";
  }

  return null;
};

type ValueCheck = ComponentTypeRules &
  Pick<AutomapColumnsContext, 'fields'> & {
    mappings: AutomapColumnMapping[];
  };

export const findValueProblem = ({
  mappings,
  componentTypes,
  canCreateTypes,
  fields,
}: ValueCheck): string | null => {
  const hasComponentTypeColumn = hasColumnType(mappings, 'component-type');
  const unknownComponentTypes = mappings.filter(
    mapping =>
      !isKnownComponentType(
        { componentTypes, canCreateTypes },
        hasComponentTypeColumn,
        mapping
      )
  );
  if (unknownComponentTypes.length > 0) {
    return `Invalid component type for columns: ${toColumnNames(unknownComponentTypes)}. ${
      canCreateTypes
        ? 'Give a type name'
        : "Use a name from the root workspace's 'componentTypes'"
    }, or set it to null and map a 'component-type' column.`;
  }

  const unknownFields = mappings.filter(
    mapping => !isKnownField(fields, mapping)
  );
  if (unknownFields.length > 0) {
    return `Unknown field for columns: ${toColumnNames(unknownFields)}. Copy name, label and type from 'fields', or set existingField to null.`;
  }

  const newCustomIdFields = canCreateTypes
    ? []
    : mappings.filter(isNewCustomIdField);
  if (newCustomIdFields.length > 0) {
    return `New fields cannot be created for columns: ${toColumnNames(newCustomIdFields)}. Set existingField to a field from 'fields'.`;
  }

  const invalidLevels = mappings.filter(
    mapping => !hasValidHierarchyLevel(mapping)
  );
  if (invalidLevels.length > 0) {
    return `Hierarchy level must be 1 or more for columns: ${toColumnNames(invalidLevels)}.`;
  }

  const hasReferenceTypeColumn = hasColumnType(mappings, 'reference-type');
  const missingReferenceTypes = mappings.filter(
    mapping => !hasReferenceType(mapping, hasReferenceTypeColumn)
  );
  if (missingReferenceTypes.length > 0) {
    return `Reference type missing for columns: ${toColumnNames(missingReferenceTypes)}. Set referenceType, or map a column as 'reference-type'.`;
  }

  return null;
};

const toReferenceType = ({ mappedTo }: AutomapColumnMapping): string | null => {
  switch (mappedTo.columnType) {
    case 'reference':
    case 'root-reference':
      return mappedTo.referenceType?.trim() || null;
    default:
      return null;
  }
};

const toReferenceSourceWorkspace = (
  { mappedTo }: AutomapColumnMapping,
  { rootWorkspace }: ReferenceSides
): IntegrationWorkspace =>
  mappedTo.columnType === 'reference' &&
  mappedTo.referenceDirection === IntegrationReferenceDirection.INCOMING
    ? mappedTo.targetWorkspace
    : rootWorkspace;

const toWorkspaceReferenceTypes = (
  { id }: IntegrationWorkspace,
  { workspaces }: AutomapColumnsContext
): string[] =>
  workspaces.find(workspace => workspace.id === id)?.referenceTypes ?? [];

const hasExistingReferenceType = (
  mapping: AutomapColumnMapping,
  sides: ReferenceSides,
  context: AutomapColumnsContext
): boolean => {
  const referenceType = toReferenceType(mapping);
  if (referenceType === null) {
    return true;
  }
  const workspace = toReferenceSourceWorkspace(mapping, sides);
  return toWorkspaceReferenceTypes(workspace, context).includes(referenceType);
};

const hasValidReferencedComponentType = (
  mapping: AutomapColumnMapping,
  sides: ReferenceSides,
  context: AutomapColumnsContext
): boolean => {
  const workspace = toReferencedWorkspace(mapping, sides);
  const componentType = toReferencedComponentType(mapping);
  if (!workspace || componentType === null) {
    return true;
  }
  const componentTypes = toWorkspaceComponentTypes(workspace, context);
  return isAllowedComponentType(
    { componentTypes, canCreateTypes: context.canCreateTypes },
    componentType
  );
};

export const findReferencedTypeProblem = (
  mappings: AutomapColumnMapping[],
  sides: ReferenceSides,
  context: AutomapColumnsContext
): string | null => {
  const invalidReferencedTypes = mappings.filter(
    mapping => !hasValidReferencedComponentType(mapping, sides, context)
  );
  if (invalidReferencedTypes.length > 0) {
    return `Invalid componentType for columns: ${toColumnNames(invalidReferencedTypes)}. ${
      context.canCreateTypes
        ? 'Give a type name'
        : "Use a name from the referenced workspace's 'componentTypes'"
    }.`;
  }

  if (context.canCreateTypes) {
    return null;
  }
  const unknownReferenceTypes = mappings.filter(
    mapping => !hasExistingReferenceType(mapping, sides, context)
  );
  if (unknownReferenceTypes.length === 0) {
    return null;
  }
  return `Unknown referenceType for columns: ${toColumnNames(unknownReferenceTypes)}. Use a name from the 'referenceTypes' of the workspace the reference starts in: 'targetWorkspace' for incoming references, otherwise the root workspace.`;
};
